import React, { useState } from 'react';

export const EXERCISE_TYPES = [
    { key: 'fill_gaps', label: 'Fill in the Gaps', icon: '✏️' },
    { key: 'fill_gaps_self_check', label: 'Fill in the Gaps (Self-Check)', icon: '✅' },
    { key: 'fill_gaps_ai', label: 'Fill in the Gaps (AI)', icon: '🤖' },
    { key: 'fill_gaps_words', label: 'Fill in the Gaps (Given Words)', icon: '🔤' },
    { key: 'fill_gaps_choice', label: 'Mixed', icon: '✳️' },
    { key: 'order_words', label: 'Order the Words', icon: '🔀' },
    { key: 'order_lines', label: 'Order (Lines)', icon: '↕️' },
    { key: 'jumbled_letters', label: 'Jumbled Letters', icon: '🔡' },
    { key: 'dictation', label: 'Dictation', icon: '🎧' },
    { key: 'voice_record', label: 'Voice Recording', icon: '🎙' },
    { key: 'voice_compare', label: 'Pronunciation Compare', icon: '🗣️' },
    { key: 'multiple_choice_short', label: 'Multiple Choice (Short)', icon: '🔘' },
    { key: 'multiple_choice_long', label: 'Multiple Choice (Long)', icon: '📋' },
    { key: 'text', label: 'Text', icon: '📄' },
    { key: 'text_slides', label: 'Text Slides', icon: '🗂️' },
    { key: 'disappearing_text', label: 'Disappearing Text', icon: '👻' },
    { key: 'essay', label: 'Essay', icon: '📝' },
    { key: 'checklist', label: 'Checklist', icon: '☑️' },
    { key: 'wordlist', label: 'Wordlist', icon: '📋' },
    { key: 'auto_wordlist', label: 'Auto Wordlist', icon: '🔄' },
    { key: 'wordlist_slides', label: 'Wordlist as Slides', icon: '🃏' },
    { key: 'slides', label: 'Slides', icon: '🖼️' },
    { key: 'powerpoint_presentation', label: 'PowerPoint Presentation', icon: '📊' },
    { key: 'sentence_slides', label: 'Sentence Slides', icon: '💬' },
    { key: 'randomizer', label: 'Randomizer', icon: '🎰' },
    { key: 'reading', label: 'Parallel Reading', icon: '📖' },
    { key: 'image_fill_gaps', label: 'Fill in the Gaps (with Images)', icon: '🖼️' },
    { key: 'image_matching', label: 'Matching (with Images)', icon: '🧩' },
    { key: 'matching_columns', label: 'Matching (columns)', icon: '🔗' },
    { key: 'sort_boxes', label: 'Sort into Boxes', icon: '📦' },
    { key: 'error_correction', label: 'Error Correction', icon: '🩹' },
    { key: 'illustration', label: 'Illustration', icon: '🖼️' },
];

export const EXERCISE_GROUPS = [
    { title: '✏️ GAPS', types: ['fill_gaps', 'fill_gaps_self_check', 'fill_gaps_ai', 'fill_gaps_words', 'fill_gaps_choice', 'error_correction', 'image_fill_gaps'] },
    { title: '📄 TEXTS', types: ['text', 'disappearing_text', 'essay', 'checklist', 'reading'] },
    { title: '📋 WORDLIST', types: ['wordlist', 'auto_wordlist'] },
    { title: '🖼️ SLIDES', types: ['slides', 'wordlist_slides', 'sentence_slides', 'text_slides', 'powerpoint_presentation', 'illustration', 'randomizer'] },
    { title: '🔘 MULTIPLE CHOICE', types: ['multiple_choice_short', 'multiple_choice_long'] },
    { title: '🔀 REORDER', types: ['order_words', 'order_lines', 'jumbled_letters'] },
    { title: '🔗 MATCHING & SORTING', types: ['matching_columns', 'image_matching', 'sort_boxes'] },
    { title: '🎧 AUDIO', types: ['dictation', 'voice_record', 'voice_compare'] },
];

/** Ensure every registered type appears in exactly one category (dev sanity check). */
export function assertExerciseGroupsCoverAllTypes(types = EXERCISE_TYPES, groups = EXERCISE_GROUPS) {
    const seen = new Map();
    for (const group of groups) {
        for (const key of group.types) {
            if (seen.has(key)) {
                console.warn(`[ExerciseTypeSelect] "${key}" is listed in both "${seen.get(key)}" and "${group.title}"`);
            }
            seen.set(key, group.title);
        }
    }
    for (const t of types) {
        if (!seen.has(t.key)) {
            console.warn(`[ExerciseTypeSelect] "${t.key}" is missing from EXERCISE_GROUPS`);
        }
    }
}

if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    assertExerciseGroupsCoverAllTypes();
}

export function TypeSelectModal({ onClose, onSelect, exerciseTypes = EXERCISE_TYPES, exerciseGroups = EXERCISE_GROUPS, zIndex = 1100 }) {
    const [q, setQ] = useState('');

    const filteredGroups = exerciseGroups.map(group => {
        const matching = group.types
            .map(typeKey => exerciseTypes.find(t => t.key === typeKey))
            .filter(t => t && (
                t.label.toLowerCase().includes(q.toLowerCase()) ||
                group.title.toLowerCase().includes(q.toLowerCase())
            ));
        return { ...group, matching };
    }).filter(group => group.matching.length > 0);

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex, background: 'rgba(15, 23, 42, 0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(12px)', padding: '20px' }} onClick={onClose}>
            <div 
                style={{
                    background: 'white',
                    width: '100%', maxWidth: '900px',
                    maxHeight: '90vh',
                    borderRadius: '32px',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    border: '1px solid rgba(255,255,255,0.1)'
                }} 
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ padding: '2rem 2.5rem', borderBottom: '1.5px solid #f1f5f9', background: '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                        <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 900, color: '#0f172a' }}>Select Exercise Type</h2>
                        <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', width: 44, height: 44, borderRadius: '14px', cursor: 'pointer', fontSize: '1.2rem', color: '#64748b', transition: 'all 0.2s' }}>✕</button>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.2rem', opacity: 0.5 }}>🔍</span>
                        <input 
                            autoFocus
                            type="text" 
                            placeholder="Search by type name or category..." 
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            style={{ 
                                width: '100%', 
                                padding: '1rem 1rem 1rem 3.5rem', 
                                borderRadius: '16px', 
                                border: '2px solid #e2e8f0', 
                                outline: 'none', 
                                fontSize: '1.1rem',
                                transition: 'all 0.2s',
                                fontWeight: 500
                            }}
                            onFocus={e => e.target.style.borderColor = '#4361ee'}
                            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                        />
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: '2rem 2.5rem', overflowY: 'auto', background: 'white', flex: 1 }} className="custom-scrollbar">
                    {filteredGroups.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔦</div>
                            <h3 style={{ margin: 0 }}>No matching exercise types found</h3>
                            <p>Try a different search term</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
                            {filteredGroups.map(group => (
                                <div key={group.title}>
                                    <div style={{ 
                                        fontSize: '0.8rem', 
                                        fontWeight: 900, 
                                        color: '#64748b', 
                                        textTransform: 'uppercase', 
                                        letterSpacing: '0.1em',
                                        marginBottom: '1rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}>
                                        {group.title}
                                        <div style={{ flex: 1, height: '1.5px', background: '#f1f5f9' }}></div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem' }}>
                                        {group.matching.map(t => (
                                            <button
                                                key={t.key}
                                                onClick={() => onSelect(t.key)}
                                                className="type-select-btn"
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 14, width: '100%',
                                                    padding: '1.25rem 1.5rem', border: '2px solid #f1f5f9', background: 'white',
                                                    borderRadius: '18px', cursor: 'pointer', textAlign: 'left',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <div style={{ 
                                                    width: 48, height: 48, borderRadius: '14px', background: '#f8fafc',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '1.6rem'
                                                }}>
                                                    {t.icon}
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{t.label}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <style>{`
                .type-select-btn:hover {
                    border-color: #4361ee !important;
                    background: #f8faff !important;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px -10px rgba(67, 97, 238, 0.2);
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #cbd5e1;
                }
            `}</style>
        </div>
    );
}
